import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle, AlertCircle, Share2, Copy, Check } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';
import { tryNativeShare } from '../utils/shareHelpers';

// Landing on this URL auto-opens the popup — sold as an "invite" so the
// recipient sees social-proof copy instead of the passive marketing pitch.
const buildShareUrl = () => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/?subscribe=1&ref=share`;
};

const Newsletter = () => {
  const { subscribe } = useSubscription();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');
  const [copyLabel, setCopyLabel] = useState('Copy invite link');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) return;
    
    setStatus('loading');
    
    try {
      await subscribe(email);
      
      setStatus('success');
      setMessage('Successfully subscribed! Watchlist and sharing are now unlocked.');
      setEmail('');
      
      // Reset after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Something went wrong. Please try again.');
      
      // Reset after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    }
  };

  const handleShare = async () => {
    const shareUrl = buildShareUrl();
    const shareData = {
      title: 'The Movie Junkie Streaming Guide',
      text: 'Join the weekly OTT + theatrical drop-list — never miss a release.',
      url: shareUrl,
    };
    const nativeUsed = await tryNativeShare(shareData);
    if (nativeUsed) return;
    // Desktop / no-share-API fallback → clipboard copy with feedback.
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyLabel('Link copied ✓');
    } catch {
      setCopyLabel('Copy failed — select the URL manually');
    }
    setTimeout(() => setCopyLabel('Copy invite link'), 2500);
  };

  return (
    <section className="mb-16" data-testid="newsletter-section">
      <div className="bg-black/90 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-2xl border border-white/10 max-w-4xl mx-auto">
        <div className="text-center space-y-6">
          <div className="flex items-center justify-center gap-3">
            <Mail className="w-8 h-8 text-white" />
            <h2 className="font-primary text-2xl md:text-4xl uppercase tracking-wide text-white">
              Weekly Newsletter
            </h2>
          </div>
          
          <p className="font-secondary text-base md:text-lg text-gray-300 max-w-xl mx-auto">
            Get the latest streaming releases delivered to your inbox every week. Never miss a new show or movie!
          </p>
          
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={status === 'loading' || status === 'success'}
              data-testid="newsletter-email-input"
              className="flex-1 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 font-secondary focus:outline-none focus:border-white/50 transition-all disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={status === 'loading' || status === 'success' || !email}
              data-testid="newsletter-submit-btn"
              className="px-8 py-3 bg-white text-black hover:bg-gray-200 font-secondary font-bold rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {status === 'loading' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Subscribing...</span>
                </>
              ) : status === 'success' ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>Subscribed!</span>
                </>
              ) : (
                <span>Subscribe</span>
              )}
            </button>
          </form>
          
          {/* Status Message */}
          {message && (
            <div className={`flex items-center justify-center gap-2 font-secondary text-sm ${
              status === 'success' ? 'text-green-400' : 'text-red-400'
            }`}>
              {status === 'success' ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              <span>{message}</span>
            </div>
          )}
          
          <p className="font-secondary text-xs text-gray-500">
            No spam, unsubscribe anytime. We respect your privacy.
          </p>

          {/* Invite-a-friend row — sits below the form so it never
              competes with the primary CTA visually. */}
          <div
            className="pt-4 border-t border-white/10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
            data-testid="newsletter-share-row"
          >
            <span className="font-secondary text-xs text-gray-400 flex items-center gap-2">
              <Share2 className="w-3.5 h-3.5" />
              Loving this? Send the newsletter to a friend.
            </span>
            <button
              type="button"
              onClick={handleShare}
              data-testid="newsletter-share-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-secondary font-semibold transition-all"
            >
              {copyLabel.startsWith('Link copied') ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
              <span>{copyLabel}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
